import React, { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";

import { ChevronRight, Menu } from "shared/assets/icons";
import { IActionDropdownItem, ActionDropdown } from "shared/components";
import {
  CardContainer,
  CardHeader,
  CardTitle,
  CardContent,
  CardContainerProps,
  StyledActionDropdown,
  CardTitleItem,
} from "./styles";
import { Spacer } from "shared/layout";
import { Icon } from "shared/styles";

import { copy } from "shared/assets";
import { isArray } from "lodash-es";

export interface ICardTitle {
  text: string;
  to?: string;
}
export interface CardProps extends CardContainerProps {
  title?: string | string[] | ICardTitle[];
  titleHeading?: keyof JSX.IntrinsicElements;
  icon?: React.ComponentType<any>;
  id?: string;
  actions?: IActionDropdownItem[];
  accentColor?: string;
  actionDataTestId?: string;
}

export const Card: React.FC<CardProps> = ({
  title,
  titleHeading = "h2",
  icon,
  children,
  actions,
  hideContainer,
  size,
  accentColor,
  actionDataTestId = "card_action_select",
  center,
  ...props
}) => {
  const titleArray: ICardTitle[] | undefined = useMemo(() => {
    if (typeof title === "string") {
      return [{ text: title }];
    }

    if (isArray(title)) {
      return title.map((text: string | ICardTitle) => (typeof text === "string" ? { text } : text));
    }
  }, [title]);

  const actionSelectProps = useMemo(
    () => ({
      label: copy.menu.open,
      options: actions || [],
      triggerIcon: Menu,
      dataTestId: actionDataTestId,
    }),
    [actions, actionDataTestId]
  );

  const isEmpty = useMemo(() => !children, [children]);

  return (
    <CardContainer hideContainer={hideContainer} {...props}>
      {!!actions?.length && title === undefined && (
        <StyledActionDropdown>
          <ActionDropdown {...actionSelectProps} placement="bottom-end" wrapOptions />
        </StyledActionDropdown>
      )}
      {title && (
        <CardHeader isEmpty={isEmpty} accentColor={accentColor} center={center}>
          <Spacer spaceDirection="horizontal" size="xs" align="center" flexWrap="nowrap">
            {icon && <Icon as={icon} size="l" />}
            <CardTitle asComponent={titleHeading} size="s">
              {titleArray?.map(({ text, to }, index) => (
                <Fragment key={index}>
                  {index !== 0 && <Icon as={ChevronRight} size="s" />}
                  {to ? (
                    <CardTitleItem isMuted={index !== titleArray.length - 1}>
                      <Link to={to}>{text}</Link>
                    </CardTitleItem>
                  ) : (
                    <CardTitleItem isMuted={index !== titleArray.length - 1}>{text}</CardTitleItem>
                  )}
                </Fragment>
              ))}
            </CardTitle>
          </Spacer>
          {!!actions?.length && (
            <ActionDropdown {...actionSelectProps} placement="bottom-end" wrapOptions />
          )}
        </CardHeader>
      )}
      <CardContent hideContainer={hideContainer || isEmpty} size={size}>
        {children}
      </CardContent>
    </CardContainer>
  );
};
